/**
 * Build-time configuration.
 *
 * `import.meta.env` values are inlined by Vite at compile time; this module is the one
 * place that reads them, so the rest of the app imports a resolved value instead of
 * the raw variable.
 */

const rawBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Origin every API request is sent to, without a trailing slash.
 *
 * Empty by default, which makes every request relative: the browser then talks to the
 * origin that served the app. That is the deployed shape (a reverse proxy puts the
 * static files and `/api` on one origin) and the development shape (Vite proxies to
 * `http://localhost:3000`) — and it is what lets the API's Origin check and its
 * HttpOnly session cookie work without cross-origin CORS.
 */
export const apiBaseUrl = rawBaseUrl.replace(/\/+$/, '');

/** `true` only in `vite dev`; production builds and `vite preview` are both false. */
export const isDev = import.meta.env.DEV;

export const appTitle = '爱出海';

/** The API's own default floor, used when this build is not told otherwise. */
const defaultMinPasswordLength = 8;

/**
 * Reads the password floor the registration form shows, falling back to the API's
 * default for anything that is not a length the server would accept.
 *
 * The API remains the authority (`AUTH_MIN_PASSWORD_LENGTH`); this value only keeps
 * the form from rejecting a password the server would take, or from promising a
 * length it would not. Exported for the spec, which exercises the fallbacks.
 */
export const resolveMinPasswordLength = (raw: string | undefined): number => {
  const value = Number(raw ?? defaultMinPasswordLength);
  return Number.isInteger(value) && value >= 8 && value <= 64
    ? value
    : defaultMinPasswordLength;
};

/** Mirrors the API's `AUTH_MIN_PASSWORD_LENGTH`; keep the two in step per deployment. */
export const authMinPasswordLength = resolveMinPasswordLength(
  import.meta.env.VITE_AUTH_MIN_PASSWORD_LENGTH,
);

/** Where better-auth mounts its endpoints, on the API (`apps/api/config/middleware.config.ts`). */
export const authBasePath = '/api/auth';
